/**
 * Sequencer Player Page
 *
 * Full-screen drag-to-order exercise for operators. Loaded outside
 * OperatorLayout (no tab bar) at /operator/practice/sequencer/:taskId.
 *
 * Flow: loading -> playing (drag steps to correct order) -> submitting -> results.
 *
 * Uses @dnd-kit for accessible, touch-friendly drag-and-drop.
 * Steps are shuffled client-side using Fisher-Yates shuffle.
 * Grading is server-side (POST /operator/practice/sequencer).
 */

import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type Modifier,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { ListOrdered, AlertTriangle, Loader2 } from 'lucide-react';
import { PlayerShell } from './components/PlayerShell';
import { SequencerStep } from './components/SequencerStep';
import { SequencerResults } from './components/SequencerResults';
import { OperatorLoadingSkeleton } from './components/OperatorLoadingSkeleton';
import { OperatorEmptyState } from './components/OperatorEmptyState';
import { useSequencer, useSubmitSequencer, usePracticeStats } from './hooks/useOperatorPractice';
import type { SequencerStep as SequencerStepType, SequencerResultItem } from '@guild/shared';

type SequencerState = 'loading' | 'playing' | 'submitting' | 'results';

/**
 * Fisher-Yates shuffle. Returns a new array.
 * Ensures the result is not in the same order as the input (retries once if identical).
 */
function fisherYatesShuffle<T>(arr: T[]): T[] {
  const shuffled = [...arr];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  // If shuffle produced same order (unlikely but possible for small arrays), shuffle again
  if (
    shuffled.length > 1 &&
    shuffled.every((item, idx) => item === arr[idx])
  ) {
    return fisherYatesShuffle(arr);
  }
  return shuffled;
}

/**
 * Vertical-axis restriction modifier.
 * Constrains drag movement to vertical only (no horizontal drift).
 * Inline implementation to avoid adding @dnd-kit/modifiers dependency.
 */
const restrictToVerticalAxis: Modifier = ({ transform }) => {
  return {
    ...transform,
    x: 0,
  };
};

export function SequencerPlayerPage() {
  const { taskId } = useParams<{ taskId: string }>();

  // --- Data fetching ---
  const { data: sequencer, isLoading, error, refetch } = useSequencer(taskId ?? null);
  const submitSequencer = useSubmitSequencer();
  const { data: stats } = usePracticeStats();

  // --- Local state ---
  const [sequencerState, setSequencerState] = useState<SequencerState>('loading');
  const [orderedSteps, setOrderedSteps] = useState<SequencerStepType[]>([]);
  const [submissionResults, setSubmissionResults] = useState<{
    score: number;
    correctPositions: number;
    totalSteps: number;
    results: SequencerResultItem[];
  } | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [submissionError, setSubmissionError] = useState<string | null>(null);

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const elapsedSecondsRef = useRef(elapsedSeconds);
  elapsedSecondsRef.current = elapsedSeconds;

  // --- Sensors for drag-and-drop ---
  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: {
      distance: 8, // Prevent accidental drags (differentiates from scroll)
    },
  });
  const keyboardSensor = useSensor(KeyboardSensor, {
    coordinateGetter: sortableKeyboardCoordinates,
  });
  const sensors = useSensors(pointerSensor, keyboardSensor);

  // --- Timer management ---
  useEffect(() => {
    if (sequencerState === 'playing') {
      timerRef.current = setInterval(() => {
        setElapsedSeconds((prev) => prev + 1);
      }, 1000);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [sequencerState]);

  // --- Initialize sequencer on data load ---
  useEffect(() => {
    if (sequencer && sequencerState === 'loading') {
      // Shuffle steps client-side (defensive — backend may return in order)
      setOrderedSteps(fisherYatesShuffle(sequencer.steps));
      setSequencerState('playing');
    }
  }, [sequencer, sequencerState]);

  // --- Build step data map for results screen ---
  const stepDataMap = useMemo(() => {
    if (!sequencer) return new Map<string, { description: string; isSafetyCritical: boolean }>();
    return new Map(
      sequencer.steps.map((s) => [
        s.id,
        { description: s.description, isSafetyCritical: s.isSafetyCritical },
      ]),
    );
  }, [sequencer]);

  // --- Drag end handler ---
  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setOrderedSteps((items) => {
      const oldIndex = items.findIndex((item) => item.id === active.id);
      const newIndex = items.findIndex((item) => item.id === over.id);
      return arrayMove(items, oldIndex, newIndex);
    });
  }, []);

  // --- Submit handler ---
  const handleSubmit = useCallback(async () => {
    if (!sequencer || !taskId) return;

    setSequencerState('submitting');

    // Stop the timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    const stepOrder = orderedSteps.map((s) => s.id);
    const currentElapsed = elapsedSecondsRef.current;

    try {
      const result = await submitSequencer.mutateAsync({
        taskId,
        stepOrder,
        elapsedSeconds: currentElapsed,
      });

      setSubmissionResults(result);
      setSequencerState('results');
      setSubmissionError(null);
    } catch (err) {
      console.error('Failed to submit sequencer:', err);
      setSubmissionError('Failed to submit. Please try again.');
      setSequencerState('playing');
    }
  }, [sequencer, taskId, orderedSteps, submitSequencer]);

  // --- Try Again handler ---
  const handleTryAgain = useCallback(() => {
    setSequencerState('loading');
    setOrderedSteps([]);
    setSubmissionResults(null);
    setElapsedSeconds(0);
    setSubmissionError(null);

    // Re-fetch for fresh data
    refetch();
  }, [refetch]);

  // --- Leave confirmation ---
  useEffect(() => {
    if (sequencerState !== 'playing') return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [sequencerState]);

  // ============================================
  // RENDER
  // ============================================

  // Loading state
  if (isLoading || (sequencerState === 'loading' && !error)) {
    return (
      <div className="min-h-screen bg-op-surface">
        <div className="mx-auto max-w-lg px-4 py-8">
          <div className="flex flex-col items-center gap-4 py-16">
            <Loader2 size={32} className="animate-spin text-guild-green" />
            <p className="text-sm font-medium text-op-text-secondary">
              Loading step sequencer...
            </p>
          </div>
          <OperatorLoadingSkeleton variant="cards" />
        </div>
      </div>
    );
  }

  // Error state
  if (error || !sequencer) {
    return (
      <div className="min-h-screen bg-op-surface">
        <OperatorEmptyState
          icon={AlertTriangle}
          title="Sequencer Not Available"
          description="This step sequencer could not be loaded. The task may not have enough steps yet."
          ctaLabel="Back to Content"
          ctaHref="/operator/content"
        />
      </div>
    );
  }

  // Results state
  if (sequencerState === 'results' && submissionResults) {
    return (
      <SequencerResults
        correctPositions={submissionResults.correctPositions}
        totalSteps={submissionResults.totalSteps}
        score={submissionResults.score}
        elapsedSeconds={elapsedSeconds}
        bestScore={stats?.sequencerBestScore ?? null}
        results={submissionResults.results}
        stepData={stepDataMap}
        onTryAgain={handleTryAgain}
        title={`${sequencer.sopTitle} — ${sequencer.taskTitle}`}
      />
    );
  }

  // Submitting state
  if (sequencerState === 'submitting') {
    return (
      <div className="min-h-screen bg-op-surface">
        <div className="flex flex-col items-center justify-center py-32">
          <Loader2 size={32} className="animate-spin text-guild-green" />
          <p className="mt-4 text-sm font-medium text-op-text-secondary">
            Checking your order...
          </p>
        </div>
      </div>
    );
  }

  // Playing state
  return (
    <PlayerShell
      title={`${sequencer.sopTitle} — ${sequencer.taskTitle}`}
      subtitle="Step Sequencer"
      current={orderedSteps.length}
      total={sequencer.totalSteps}
      progressLabel="Steps"
      inProgress
      backTo="/operator/content"
      showTimer
      elapsedSeconds={elapsedSeconds}
    >
      {/* Submission error banner */}
      {submissionError && (
        <div className="mb-4 rounded-2xl bg-red-50 p-4 ring-1 ring-red-200">
          <p className="text-sm font-medium text-red-700">{submissionError}</p>
          <button
            type="button"
            onClick={handleSubmit}
            className="mt-2 text-sm font-semibold text-red-700 underline"
          >
            Retry submission
          </button>
        </div>
      )}

      {/* Instructions */}
      <div className="mb-4 flex items-center gap-2 rounded-2xl bg-guild-green/5 p-3">
        <ListOrdered size={18} strokeWidth={1.5} className="flex-shrink-0 text-guild-green" />
        <p className="text-xs font-medium text-guild-green">
          Drag the steps into the correct order, then check your answer.
        </p>
      </div>

      {/* Sortable step list */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
        modifiers={[restrictToVerticalAxis]}
      >
        <SortableContext
          items={orderedSteps.map((s) => s.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className="space-y-2" role="list" aria-label="Reorderable steps">
            {orderedSteps.map((step, index) => (
              <SequencerStep
                key={step.id}
                id={step.id}
                description={step.description}
                isSafetyCritical={step.isSafetyCritical}
                position={index + 1}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {/* Check Order button */}
      <div className="mt-6">
        <button
          type="button"
          onClick={handleSubmit}
          className="flex w-full min-h-[52px] items-center justify-center gap-2 rounded-xl bg-guild-green px-6 py-3.5 text-sm font-semibold text-white shadow-sm active:scale-[0.98] active:bg-guild-green/90 transition-all duration-150 focus-visible:ring-2 focus-visible:ring-guild-green focus-visible:ring-offset-2"
        >
          Check Order
        </button>
      </div>
    </PlayerShell>
  );
}
